import React from "react";
import {
  ArrowLeft,
  Users,
  Building2,
  Target,
  FileText,
  Layout,
  BarChart,
  Briefcase,
} from "lucide-react";

const TalentConnectPage = () => {
  const steps = [
    {
      step: "01",
      title: "Create Profile",
      desc: "Sign up and build your employer brand page to attract top talent.",
      color: "bg-blue-50 text-blue-700",
    },
    {
      step: "02",
      title: "Post Opportunities",
      desc: "Publish jobs and internships to thousands of colleges instantly.",
      color: "bg-indigo-50 text-indigo-700",
    },
    {
      step: "03",
      title: "Screen Candidates",
      desc: "Use AI filters and assessments to find the perfect match.",
      color: "bg-purple-50 text-purple-700",
    },
    {
      step: "04",
      title: "Hire",
      desc: "Roll out offers and manage onboarding seamlessly.",
      color: "bg-green-50 text-green-700",
    },
  ];

  const features = [
    {
      title: "2 Lakh+ Verified Profiles",
      desc: "Access a massive pool of pre-verified student profiles.",
      icon: <Users className="text-blue-600" />,
    },
    {
      title: "Campus Drive Management",
      desc: "Conduct virtual or physical campus drives with ease.",
      icon: <Building2 className="text-blue-600" />,
    },
    {
      title: "AI Candidate Matching",
      desc: "Smart algorithms to suggest the best candidates for your job.",
      icon: <Target className="text-blue-600" />,
    },
    {
      title: "Resume Database",
      desc: "Search and download resumes with advanced filters.",
      icon: <FileText className="text-blue-600" />,
    },
    {
      title: "Employer Branding",
      desc: "Showcase your company culture to attract the best talent.",
      icon: <Layout className="text-blue-600" />,
    },
    {
      title: "Advanced Analytics",
      desc: "Track your hiring funnel and optimize your recruitment process.",
      icon: <BarChart className="text-blue-600" />,
    },
  ];

  return (
    <div className="bg-white min-h-screen font-sans text-slate-900">
      {/* Navigation Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-blue-600" />
            <span className="font-bold text-xl tracking-tight">
              Talent<span className="text-blue-600">Connect</span>
            </span>
          </div>
          <button
            onClick={() =>
              window.open(
                "https://talentconnect.freshmentapp.com/register",
                "_blank",
              )
            }
            className="flex xsm:hidden bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-500/20"
          >
            Login / Sign Up
          </button>
        </div>
      </div>

      {/* 1) HERO SECTION */}
      <div className="bg-blue-50 hero-pattern py-28 xl:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-block py-1 px-3 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold mb-6 tracking-wide uppercase">
              For Employers & Recruiters
            </span>
            <h1 className="text-5xl xl:text-4xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight">
              Join Talent Connect
            </h1>
            <p className="text-2xl font-bold text-gray-700 mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
              The ultimate hiring platform for modern companies
            </p>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl mx-auto">
              Connect with India's brightest fresh graduates. Streamline your
              campus hiring and find your next star employee today.
            </p>
            <div className="flex flex-row xsm:flex-col justify-center gap-4">
              <button
                onClick={() =>
                  window.open(
                    "https://talentconnect.freshmentapp.com/register",
                    "_blank",
                  )
                }
                className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-3.5 px-8 rounded-lg shadow-lg transition-transform hover:-translate-y-1"
              >
                Start Hiring Now
              </button>
              <button
                onClick={() =>
                  (window.location.href = "mailto:sales@freshmentapp.com")
                }
                className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-bold py-3.5 px-8 rounded-lg transition-colors"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 2) PRODUCT OVERVIEW */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">
              Why Top Companies Choose Talent Connect
            </h2>
            <p className="text-gray-600 mt-4 max-w-3xl mx-auto text-lg">
              We simplify the complex process of campus hiring. From sourcing to
              offer letters, Talent Connect provides the tools you need to build
              a world-class team.
            </p>
          </div>
        </div>
      </div>

      {/* 3) FEATURES */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Platform Features
            </h2>
          </div>

          <div className="grid grid-cols-3 xl:grid-cols-2 xsm:grid-cols-1 gap-8">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {f.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 4) WORKFLOW - 4 STEPS */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">
              Hiring in 4 Easy Steps
            </h2>
          </div>

          <div className="grid grid-cols-4 xsm:grid-cols-1 gap-6 relative">
            {/* Connector Line (Desktop) */}
            <div className="block xsm:hidden absolute top-8 left-0 w-full h-0.5 bg-gray-200 -z-10" />

            {steps.map((item, idx) => (
              <div
                key={idx}
                className="relative bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-4 ${item.color}`}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 5) FINAL CTA */}
      <div className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 text-center">
          <h2 className="text-4xl xsm:text-3xl font-bold text-gray-900 mb-8">
            Ready to build your dream team?
          </h2>
          <div className="flex flex-row xsm:flex-col justify-center gap-4">
            <button
              onClick={() =>
                window.open(
                  "https://talentconnect.freshmentapp.com/register",
                  "_blank",
                )
              }
              className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-transform hover:-translate-y-1 text-lg"
            >
              Get Started for Free
            </button>
            <button
              onClick={() =>
                (window.location.href = "mailto:sales@freshmentapp.com")
              }
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-bold py-4 px-10 rounded-lg transition-colors text-lg"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentConnectPage;
